import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { VehicleState } from '../model/vehicleDriving';

/**
 * 게임패드 진동.
 *
 * 브라우저 API를 여기 한 곳에만 둡니다(Three 코드에서 직접 부르지 않습니다).
 * 60fps로 playEffect를 호출하면 큐가 밀려 진동이 끊기므로 약 12Hz로 갱신하고,
 * 세기가 거의 같으면 아예 보내지 않습니다.
 *
 * vibrationActuator는 아직 브라우저마다 다릅니다. 기능 감지에 실패하거나 Promise가
 * 거부되면 조용히 넘어갑니다 — 진동은 없어도 주행에 문제가 없어야 합니다.
 */

const UPDATE_INTERVAL_MS = 80;
const MIN_CHANGE = 0.08;

/**
 * 진동 actuator는 브라우저마다 노출 형태가 다릅니다(표준 타입에는 playEffect/reset이
 * 선택적으로만 있거나 아예 없습니다). 기능 감지로 쓰기 위해 느슨한 타입으로 좁힙니다.
 */
type VibrationActuatorLike = {
  playEffect?: (type: string, options: Record<string, number>) => Promise<unknown>;
  reset?: () => Promise<unknown>;
};

type VibrationCapableGamepad = Omit<Gamepad, 'vibrationActuator'> & {
  vibrationActuator?: VibrationActuatorLike;
};

export function useGamepadHaptics(
  enabled: boolean,
  gamepadIndexRef: RefObject<number | null>,
  vehicleStateRef: RefObject<VehicleState>
): void {
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || navigator.getGamepads === undefined) {
      return undefined;
    }

    let lastStrong = -1;
    let lastWeak = -1;
    let stopped = false;
    let activePadIndex = gamepadIndexRef.current;
    let lastSpawnCount = vehicleStateRef.current.spawnCount;
    let lastCollisionSequence = vehicleStateRef.current.collision.sequence;
    let collisionPulse = 0;

    const resolvePad = (): VibrationCapableGamepad | null => {
      const index = gamepadIndexRef.current;
      if (index === null) {
        return null;
      }
      const pad = navigator.getGamepads()[index] as VibrationCapableGamepad | null;
      return pad !== null && pad.connected ? pad : null;
    };

    const send = (pad: VibrationCapableGamepad, strong: number, weak: number) => {
      const actuator = pad.vibrationActuator;
      if (actuator?.playEffect === undefined) {
        return;
      }
      actuator
        .playEffect('dual-rumble', {
          startDelay: 0,
          // 다음 갱신까지 이어지도록 조금 길게 줍니다. 짧으면 진동이 끊겨서 덜덜거립니다.
          duration: UPDATE_INTERVAL_MS * 1.6,
          strongMagnitude: strong,
          weakMagnitude: weak,
        })
        .catch(() => {
          /* 권한·미지원·사용자 제스처 부족. 진동만 포기하고 주행은 계속합니다. */
        });
    };

    const timer = window.setInterval(() => {
      const pad = resolvePad();
      if (pad === null) {
        activePadIndex = null;
        collisionPulse = 0;
        return;
      }
      const state = vehicleStateRef.current;
      if (activePadIndex !== pad.index) {
        // 새로 연결된 패드에서 과거 충돌을 뒤늦게 재생하지 않습니다.
        activePadIndex = pad.index;
        lastStrong = -1;
        lastWeak = -1;
        lastSpawnCount = state.spawnCount;
        lastCollisionSequence = state.collision.sequence;
        collisionPulse = 0;
      }

      if (state.spawnCount !== lastSpawnCount) {
        // 재센터·코스 변경·이탈 리스폰 뒤에 이전 충돌 진동이 출발선까지 남지 않습니다.
        lastSpawnCount = state.spawnCount;
        lastCollisionSequence = state.collision.sequence;
        collisionPulse = 0;
        lastStrong = 0;
        lastWeak = 0;
        pad.vibrationActuator?.reset?.().catch(() => {});
        return;
      }

      const collisionTriggered = state.collision.sequence !== lastCollisionSequence;
      collisionPulse *= 0.56;
      if (collisionTriggered) {
        lastCollisionSequence = state.collision.sequence;
        collisionPulse = Math.max(collisionPulse, state.collision.strength);
      }
      const speedRatio = Math.min(1, Math.abs(state.speed) / 60);

      /*
       * 강한 쪽은 타이어 슬립·보조 개입과 충돌, 약한 쪽은 노면과 충돌 여운입니다.
       * 브라우저에 따라 새 effect가 기존 effect를 덮으므로 반드시 한 호출로 혼합합니다.
       */
      const baseStrong = Math.min(
        1,
        state.slipIntensity * 0.8 + (state.absActive ? 0.35 : 0) + (state.tcsActive ? 0.12 : 0)
      );
      const baseWeak = Math.min(1, state.roadImpact * 0.75 + speedRatio * 0.12);
      const strong = Math.min(1, baseStrong + collisionPulse * 0.92);
      const weak = Math.min(1, baseWeak + collisionPulse * 0.68);

      if (
        !collisionTriggered
        && Math.abs(strong - lastStrong) < MIN_CHANGE
        && Math.abs(weak - lastWeak) < MIN_CHANGE
      ) {
        return;
      }
      lastStrong = strong;
      lastWeak = weak;
      if (strong <= 0.02 && weak <= 0.02) {
        pad.vibrationActuator?.reset?.().catch(() => {});
        return;
      }
      send(pad, strong, weak);
    }, UPDATE_INTERVAL_MS);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      const pad = resolvePad();
      if (pad !== null && stopped) {
        // 주행을 나가거나 패드를 뽑으면 진동이 남지 않게 끕니다.
        pad.vibrationActuator?.reset?.().catch(() => {});
      }
    };
  }, [enabled, gamepadIndexRef, vehicleStateRef]);
}
